import { CloseIcon } from "~/assets/icons/Close";
import { GetSmallUrl } from "~/utils/images";
import { BigButton } from "../BigButton";
import { formatPrice } from "~/utils/strings";
import { Link, useFetcher, useNavigate } from "@remix-run/react";
import { RightPanelWrapper } from "../RightPanelWrapper";
import { useModal } from "~/context/Modal";
import { CartItem } from "../../../../types/CartItem";
import { LinkTo } from "~/assets/icons/LinkTo";
import { INTENTS } from "~/utils/intents";
import { calculatePrice } from "~/utils/price";
import { CrossSellKV } from "../../../../types/KV/ProductDetailsSchema";

export const AddedToShoppingCart = ({
  item,
  isCalendarItem,
  crossSells,
}: {
  item?: CartItem;
  isCalendarItem: boolean;
  crossSells: CrossSellKV[];
}) => {
  const { shoppingCartVisible, setShoppingCartVisible } = useModal();
  const fetcher = useFetcher();
  const navigate = useNavigate();
  if (shoppingCartVisible !== "addedToShoppingCart") return null;
  if (item === undefined) return null;

  const priceInfo = calculatePrice(
    item.product.priceFrom,
    item.product.discount,
  );

  return (
    <RightPanelWrapper
      isVisible={shoppingCartVisible === "addedToShoppingCart"}
      onClickOutside={() => setShoppingCartVisible("hidden")}
    >
      <div className="flex h-full flex-col">
        <div className="relative mt-4 flex flex-row items-center justify-center text-xl font-bold">
          Vöru bætt við körfu
          <div
            className="absolute right-4 cursor-pointer"
            onClick={() => {
              setShoppingCartVisible("hidden");
            }}
          >
            <CloseIcon size="24" />
          </div>
        </div>

        <div className="flex h-full flex-col">
          <div className="flex-grow overflow-y-scroll">
            <div className="mt-4 flex flex-col overflow-y-auto pb-10 md:mt-10">
              <div>
                <div className="mt-4 flex flex-row items-center gap-4">
                  <div className="mr-6">
                    <img
                      src={GetSmallUrl(item.product.image.id)}
                      className="h-16"
                      alt="vara"
                    />
                  </div>
                  <div className="flex w-full flex-row items-center justify-between">
                    <div>
                      <div className="flex-1">{item.product.title}</div>
                      <div className="text-xs">
                        {item.product.variation?.title}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-row items-center justify-end gap-x-2 text-lg font-bold">
                  <div
                    className={`${priceInfo.hasDiscount ? "line-through" : ""}`}
                  >
                    {formatPrice(item.product.priceFrom)}
                  </div>

                  {priceInfo.hasDiscount && (
                    <div className="text-lg font-bold text-[#FF3636]">
                      {formatPrice(priceInfo.priceWithDiscount)}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {isCalendarItem && (
              <div className="flex-grow">
                <div className="mt-2 flex flex-row items-center justify-center gap-10 text-center text-base font-bold md:mt-6">
                  Takk fyrir að taka þátt í jóladagatalinu okkar
                </div>
                <div className="mt-2 flex flex-row items-center justify-center gap-10 text-center text-base md:mt-6">
                  Það er frí sending á Dropp afhendingastað á öllum vörum í
                  dagatalinu. Má bjóða þér að nýta tækifærið og halda áfram að
                  versla eða viltu klára kaupin?
                </div>
              </div>
            )}
            <div className="flex-grow">
              {crossSells.length > 0 && (
                <div className="mt-2 flex flex-row items-center justify-center gap-10 text-center text-base font-bold md:mt-6">
                  Má bjóða þér? Við mælum með eftirfarandi{" "}
                  {crossSells.length === 1 ? "vöru" : "vörum"} sem gætu líka
                  hentað þér.
                </div>
              )}
              <div className="flex flex-row items-center justify-center">
                {crossSells.slice(0, 2).map((crossSell) => (
                  <div
                    key={crossSell.id}
                    className="mb-10 flex flex-col items-center justify-center"
                  >
                    {crossSell.externalId === undefined ? (
                      <div className="flex flex-col items-center gap-4">
                        <CrossSellView crossSell={crossSell} />
                        <Link
                          to={`/product/${crossSell.slug}`}
                          className="flex w-fit flex-row items-center gap-2 bg-[#EFD4D7] px-[6px] text-xl font-medium"
                          onClick={() => {
                            setShoppingCartVisible("hidden");
                          }}
                        >
                          Skoða
                          <LinkTo fill={"#0E0F0F"} />
                        </Link>
                      </div>
                    ) : (
                      <fetcher.Form
                        method="post"
                        className="mt-4 flex flex-col items-center gap-4"
                      >
                        <input
                          hidden
                          name="item"
                          value={JSON.stringify(crossSell)}
                        />
                        <input
                          hidden
                          name="intent"
                          value={INTENTS.ADD_CROSS_SELL_TO_CART}
                        />
                        <CrossSellView crossSell={crossSell} />
                        <button
                          onClick={(e) => {
                            fetcher.submit(e.currentTarget.form);
                            setShoppingCartVisible("hidden");
                          }}
                          className={
                            "flex w-fit flex-row items-center gap-2 bg-[#EFD4D7] px-[6px] text-xl font-medium"
                          }
                        >
                          Kaupa
                          <LinkTo fill={"#0E0F0F"} />
                        </button>
                      </fetcher.Form>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-8 mt-4 flex w-full flex-col gap-y-4">
            <BigButton
              state="enabled"
              type="light"
              onClick={() => {
                setShoppingCartVisible("hidden");
                if (isCalendarItem) {
                  navigate("/shop");
                }
              }}
            >
              Halda áfram að versla
            </BigButton>
            <BigButton
              state="enabled"
              onClick={() => {
                navigate("/checkout");
              }}
            >
              Klára kaup
            </BigButton>
          </div>
        </div>
      </div>
    </RightPanelWrapper>
  );
};

const CrossSellView = ({ crossSell }: { crossSell: CrossSellKV }) => {
  return (
    <div key={crossSell.id}>
      <div className="mr-0">
        <img
          src={GetSmallUrl(crossSell.imageUrl)}
          className="h-40"
          alt="vara"
        />
      </div>
      <div className="flex w-full flex-row items-center justify-between">
        <div className="flex w-full flex-col items-center justify-between">
          <div className="flex-1 text-center">{crossSell.name}</div>
          <div className="text-center text-base font-semibold">
            {formatPrice(crossSell.priceFrom)}
          </div>
        </div>
      </div>
    </div>
  );
};
